import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  FileText, Plus, Trash2, Edit, Building2, Layers, FolderOpen,
  CheckSquare, FileDown, AlertTriangle, Loader2, ArrowLeft, ArrowRight,
  Settings2, ChevronRight, PlusCircle, X, Calculator, Copy, Check, Lock,
  Save, FolderOpen as FolderOpenIcon, Star, Clock, ChevronDown
} from 'lucide-react';
import { calculateIntake, validateIntake, ALLOWED_FREQUENCIES } from '../lib/uwCalculations';
import { formatCurrency } from '../lib/formatters';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ['Company', 'Tile', 'Contact', 'Templates', 'Wizard', 'Review & Generate'];

// ADMIN PANEL

const TemplateAdmin = ({ getAuthHeaders }) => {
  const [templates, setTemplates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', company: 'PFS', tile: 'contracts', google_doc_id: '', placeholders: [], active: true });
  const [loading, setLoading] = useState(true);
  const [registry, setRegistry] = useState({});
  const [customPh, setCustomPh] = useState('');
  // Phase B: PDF upload state
  const [pdfUploadOpen, setPdfUploadOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfForm, setPdfForm] = useState({ name: '', company: 'PFS', tile: 'contracts' });
  const [pdfUploading, setPdfUploading] = useState(false);
  // Phase B: PDF mapping state
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mappingTemplate, setMappingTemplate] = useState(null);
  const [mappingFields, setMappingFields] = useState([]);
  const [savingMappings, setSavingMappings] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const [tRes, rRes] = await Promise.all([
        axios.get(`${API}/uw/templates`, { headers: getAuthHeaders() }),
        axios.get(`${API}/uw/placeholder-registry`, { headers: getAuthHeaders() }),
      ]);
      setTemplates(tRes.data);
      setRegistry(rRes.data.placeholders || {});
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/uw/templates/${editing.id}`, form, { headers: getAuthHeaders() });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/uw/templates`, form, { headers: getAuthHeaders() });
        toast.success('Template created');
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ name: '', company: 'PFS', tile: 'contracts', google_doc_id: '', placeholders: [], active: true });
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/uw/templates/${id}`, { headers: getAuthHeaders() });
      toast.success('Template deleted');
      fetchTemplates();
    } catch { toast.error('Failed to delete'); }
  };

  const handleToggle = async (tpl) => {
    try {
      await axios.put(`${API}/uw/templates/${tpl.id}`, { active: !tpl.active }, { headers: getAuthHeaders() });
      setTemplates(prev => prev.map(t => t.id === tpl.id ? { ...t, active: !t.active } : t));
      toast.success(`${tpl.name} ${!tpl.active ? 'activated' : 'deactivated'}`);
    } catch { toast.error('Failed to toggle'); }
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setForm({ name: tpl.name, company: tpl.company, tile: tpl.tile, google_doc_id: tpl.google_doc_id, placeholders: tpl.placeholders || [], active: tpl.active });
    setDialogOpen(true);
  };

  // Phase B: PDF upload
  const handlePdfUpload = async () => {
    if (!pdfFile || !pdfForm.name) return;
    setPdfUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', pdfFile);
      fd.append('company', pdfForm.company);
      fd.append('tile', pdfForm.tile);
      fd.append('name', pdfForm.name);
      const res = await axios.post(`${API}/uw/templates/upload-pdf`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`PDF uploaded: ${res.data.detected_field_count} fields detected`);
      setPdfUploadOpen(false);
      setPdfFile(null);
      setPdfForm({ name: '', company: 'PFS', tile: 'contracts' });
      fetchTemplates();
      // Auto-open mapping if fields were detected
      if (res.data.has_acroform_fields) {
        openPdfMapping(res.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setPdfUploading(false); }
  };

  // Phase B: Open mapping dialog
  const openPdfMapping = async (tpl) => {
    try {
      const res = await axios.get(`${API}/uw/templates/${tpl.id}/pdf-fields`, { headers: getAuthHeaders() });
      setMappingTemplate(res.data);
      // Build initial mapping state from existing or detected fields
      const existing = {};
      (res.data.pdf_field_mappings || []).forEach(m => { existing[m.pdf_field_name] = m; });
      setMappingFields((res.data.detected_pdf_fields || []).map(f => ({
        pdf_field_name: f,
        placeholder_key: existing[f]?.placeholder_key || '',
        required: existing[f]?.required || false,
        transform: existing[f]?.transform || '',
        default_value: existing[f]?.default_value || '',
      })));
      setMappingOpen(true);
    } catch (e) {
      toast.error('Failed to load PDF fields');
    }
  };

  // Phase B: Save mappings
  const handleSaveMappings = async () => {
    if (!mappingTemplate) return;
    setSavingMappings(true);
    try {
      const payload = mappingFields.filter(m => m.placeholder_key).map(m => ({
        pdf_field_name: m.pdf_field_name,
        placeholder_key: m.placeholder_key,
        required: m.required,
        transform: m.transform || null,
        default_value: m.default_value || null,
      }));
      const res = await axios.put(`${API}/uw/templates/${mappingTemplate.template_id}/pdf-mappings`, payload, { headers: getAuthHeaders() });
      toast.success(`${res.data.mapped_fields} fields mapped`);
      setMappingOpen(false);
      fetchTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save mappings');
    } finally { setSavingMappings(false); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Settings2 className="h-5 w-5" />Template Admin</CardTitle>
            <CardDescription>Manage Google Doc templates for document generation (uw-toolkit-doc-generator@mca-portal-482723.iam.gserviceaccount.com)</CardDescription>
          </div>
          <Button size="sm" onClick={() => { setEditing(null); setForm({ name: '', company: 'PFS', tile: 'contracts', google_doc_id: '', placeholders: [], active: true }); setDialogOpen(true); }} data-testid="add-template-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Google Doc
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setPdfUploadOpen(true)} data-testid="upload-pdf-btn">
            <Plus className="h-4 w-4 mr-1" /> Upload PDF
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-gray-500"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Loading...</div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No templates yet. Click "Add Template" to get started.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Tile</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Fields</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map(tpl => (
                <TableRow key={tpl.id}>
                  <TableCell className="font-medium">{tpl.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">
                      {tpl.template_type === 'pdf_acroform' ? 'PDF' : 'Doc'}
                    </Badge>
                  </TableCell>
                  <TableCell><Badge variant="outline">{tpl.company}</Badge></TableCell>
                  <TableCell><Badge variant="secondary" className="text-xs">{tpl.tile === 'contracts' ? 'Contracts' : 'ZBL'}</Badge></TableCell>
                  <TableCell className="font-mono text-xs text-gray-500 max-w-[160px] truncate">
                    {tpl.template_type === 'pdf_acroform' ? (tpl.original_filename || 'PDF') : tpl.google_doc_id}
                  </TableCell>
                  <TableCell><span className="text-xs text-gray-500">{(tpl.placeholders || []).length}</span></TableCell>
                  <TableCell>
                    <Switch checked={tpl.active} onCheckedChange={() => handleToggle(tpl)} data-testid={`toggle-${tpl.id}`} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {tpl.template_type === 'pdf_acroform' && (
                        <Button size="sm" variant="outline" onClick={() => openPdfMapping(tpl)} title="Map PDF fields" data-testid={`map-${tpl.id}`}><Layers className="h-3 w-3" /></Button>
                      )}
                      {tpl.template_type !== 'pdf_acroform' && (
                        <Button size="sm" variant="outline" onClick={() => openEdit(tpl)} data-testid={`edit-${tpl.id}`}><Edit className="h-3 w-3" /></Button>
                      )}
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(tpl.id)} data-testid={`delete-${tpl.id}`}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'Add'} Template</DialogTitle>
            <DialogDescription>Configure a Google Doc template for document generation</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template Name <span className="text-red-500">*</span></Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., MCA Agreement" data-testid="template-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company</Label>
                <Select value={form.company} onValueChange={v => setForm({ ...form, company: v })}>
                  <SelectTrigger data-testid="template-company-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PFS">PFS</SelectItem>
                    <SelectItem value="DIB">DIB</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tile</Label>
                <Select value={form.tile} onValueChange={v => setForm({ ...form, tile: v })}>
                  <SelectTrigger data-testid="template-tile-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contracts">Contracts & Addendums</SelectItem>
                    <SelectItem value="zbl">ZBLs & Payoff Letters</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Google Doc ID <span className="text-red-500">*</span></Label>
              <Input value={form.google_doc_id} onChange={e => setForm({ ...form, google_doc_id: e.target.value })} placeholder="e.g., 1AbCdEf..." className="font-mono text-sm" data-testid="template-docid-input" />
              <p className="text-xs text-gray-500 mt-1">Found in the Google Doc URL after /d/</p>
            </div>
            <div>
              <Label>Placeholders <span className="text-gray-400 text-xs font-normal ml-1">Select the {'{{PLACEHOLDERS}}'} used in this doc</span></Label>
              <div className="border rounded-md max-h-48 overflow-y-auto p-2 mt-1 space-y-1">
                {[...new Set([...Object.keys(registry), ...(form.placeholders || [])])].sort().map(key => (
                  <label key={key} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer text-xs">
                    <input
                      type="checkbox"
                      checked={(form.placeholders || []).includes(key)}
                      onChange={() => {
                        setForm(prev => ({
                          ...prev,
                          placeholders: (prev.placeholders || []).includes(key)
                            ? (prev.placeholders || []).filter(p => p !== key)
                            : [...(prev.placeholders || []), key]
                        }));
                      }}
                      className="rounded"
                    />
                    <span className="font-mono">{key}</span>
                    <span className="text-gray-400 ml-auto">{registry[key] || 'custom'}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  value={customPh}
                  onChange={e => setCustomPh(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                  placeholder="NEW_PLACEHOLDER"
                  className="h-7 text-xs font-mono flex-1"
                  data-testid="custom-placeholder-input"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && customPh.length > 1) {
                      e.preventDefault();
                      if (!(form.placeholders || []).includes(customPh)) {
                        setForm(prev => ({ ...prev, placeholders: [...(prev.placeholders || []), customPh] }));
                      }
                      setCustomPh('');
                    }
                  }}
                />
                <Button
                  type="button" size="sm" variant="outline" className="h-7 px-2"
                  disabled={!customPh || customPh.length < 2}
                  data-testid="add-custom-placeholder-btn"
                  onClick={() => {
                    if (customPh && !(form.placeholders || []).includes(customPh)) {
                      setForm(prev => ({ ...prev, placeholders: [...(prev.placeholders || []), customPh] }));
                    }
                    setCustomPh('');
                  }}
                ><PlusCircle className="h-3 w-3 mr-1" />Add</Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">{(form.placeholders || []).length} selected</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!form.name || !form.google_doc_id} data-testid="save-template-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase B: PDF Upload Dialog */}
      <Dialog open={pdfUploadOpen} onOpenChange={setPdfUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Fillable PDF Template</DialogTitle>
            <DialogDescription>Upload a PDF with AcroForm fields. Fields will be auto-detected.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template Name <span className="text-red-500">*</span></Label>
              <Input value={pdfForm.name} onChange={e => setPdfForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., MCA Agreement PDF" data-testid="pdf-template-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Company</Label>
                <Select value={pdfForm.company} onValueChange={v => setPdfForm(p => ({ ...p, company: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PFS">PFS</SelectItem>
                    <SelectItem value="DIB">DIB</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tile</Label>
                <Select value={pdfForm.tile} onValueChange={v => setPdfForm(p => ({ ...p, tile: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contracts">Contracts & Addendums</SelectItem>
                    <SelectItem value="zbl">ZBLs & Payoff Letters</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>PDF File <span className="text-red-500">*</span></Label>
              <Input type="file" accept=".pdf" onChange={e => setPdfFile(e.target.files[0])} data-testid="pdf-file-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdfUploadOpen(false)}>Cancel</Button>
            <Button onClick={handlePdfUpload} disabled={!pdfFile || !pdfForm.name || pdfUploading} data-testid="upload-pdf-submit">
              {pdfUploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Uploading...</> : 'Upload & Detect Fields'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase B: PDF Field Mapping Dialog */}
      <Dialog open={mappingOpen} onOpenChange={setMappingOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Map PDF Fields — {mappingTemplate?.template_name}</DialogTitle>
            <DialogDescription>
              {mappingTemplate?.detected_field_count || 0} AcroForm fields detected. Map each to a placeholder key.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {mappingFields.map((mf, idx) => (
              <div key={mf.pdf_field_name} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-sm">
                <span className="font-mono text-xs w-40 truncate flex-shrink-0" title={mf.pdf_field_name}>{mf.pdf_field_name}</span>
                <ArrowRight className="h-3 w-3 text-gray-400 flex-shrink-0" />
                <Select
                  value={mf.placeholder_key || '__none__'}
                  onValueChange={v => {
                    setMappingFields(prev => prev.map((f, i) => i === idx ? { ...f, placeholder_key: v === '__none__' ? '' : v } : f));
                  }}
                >
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid={`mapping-select-${idx}`}><SelectValue placeholder="Select placeholder..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Unmapped —</SelectItem>
                    {(mappingTemplate?.available_placeholders || []).map(pk => (
                      <SelectItem key={pk} value={pk}>{pk}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={mf.transform || '__none__'}
                  onValueChange={v => {
                    setMappingFields(prev => prev.map((f, i) => i === idx ? { ...f, transform: v === '__none__' ? '' : v } : f));
                  }}
                >
                  <SelectTrigger className="h-8 text-xs w-24"><SelectValue placeholder="Transform" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    <SelectItem value="currency">Currency</SelectItem>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="uppercase">Uppercase</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="percent">Percent</SelectItem>
                    <SelectItem value="ssn_last4">SSN Last 4</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500 pt-2">
            {mappingFields.filter(m => m.placeholder_key).length} / {mappingFields.length} fields mapped
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMappingOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveMappings} disabled={savingMappings} data-testid="save-mappings-btn">
              {savingMappings ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving...</> : 'Save Mappings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ─── STEP COMPONENTS ─────────────────────────────────────────────────────────

const StepCompany = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
    {[{ key: 'PFS', name: 'Power Funding Solutions INC' }, { key: 'DIB', name: 'DIB Capital INC' }].map(c => (
      <Card
        key={c.key}
        className={`cursor-pointer transition-all hover:shadow-md ${value === c.key ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}
        onClick={() => onChange(c.key)}
        data-testid={`company-${c.key}`}
      >
        <CardContent className="p-6 text-center">
          <Building2 className={`h-8 w-8 mx-auto mb-3 ${value === c.key ? 'text-blue-600' : 'text-gray-400'}`} />
          <h3 className="font-semibold text-lg">{c.key}</h3>
          <p className="text-xs text-gray-500 mt-1">{c.name}</p>
        </CardContent>
      </Card>
    ))}
  </div>
);

const StepTile = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
    {[{ key: 'contracts', name: 'Contracts & Addendums', icon: FileText }, { key: 'zbl', name: 'ZBLs & Payoff Letters', icon: FolderOpen }].map(t => (
      <Card
        key={t.key}
        className={`cursor-pointer transition-all hover:shadow-md ${value === t.key ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}
        onClick={() => onChange(t.key)}
        data-testid={`tile-${t.key}`}
      >
        <CardContent className="p-6 text-center">
          <t.icon className={`h-8 w-8 mx-auto mb-3 ${value === t.key ? 'text-blue-600' : 'text-gray-400'}`} />
          <h3 className="font-semibold">{t.name}</h3>
        </CardContent>
      </Card>
    ))}
  </div>
);

// ─── STEP 2: CONTACT SELECTOR ────────────────────────────────────────────────

const CONTACT_FIELD_MAP = {
  company_name:  ['BIZ_NAME', 'BUSINESS_NAME', 'MERCHANT_NAME'],
  owner_name:    ['MERCHANT_NAME', 'OWNER_NAME'],
  owner_first:   ['OWNER_FIRST', 'CONTACT_FIRST_NAME'],
  owner_last:    ['OWNER_LAST', 'CONTACT_LAST_NAME'],
  phone:         ['PHONE'],
  email:         ['EMAIL'],
  address:       ['BIZ_ADDRESS', 'BUSINESS_ADDRESS', 'M_ADD'],
  city:          ['BIZ_CITY', 'M_CIT'],
  state:         ['BIZ_STATE', 'M_ST'],
  zip:           ['BIZ_ZIP', 'M_ZIP'],
  ein:           ['EIN'],
  dba:           ['DBA'],
};

/** Convert raw contact prefill → { wizardKey: value } map */
function mapContactToWizard(prefill) {
  const result = {};
  for (const [field, keys] of Object.entries(CONTACT_FIELD_MAP)) {
    const val = prefill[field];
    if (!val) continue;
    for (const k of keys) result[k] = val;
  }
  return result;
}

const CREATE_FORM_DEFAULTS = {
  company_name: '', name: '', phone: '', email: '',
  business_address: '', city: '', state: '', zip: '', ein: '',
};

const StepContact = ({ onContactSelect, onDealSelect, getAuthHeaders }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDeals, setContactDeals] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [loadingDeals, setLoadingDeals] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(CREATE_FORM_DEFAULTS);
  const [creating, setCreating] = useState(false);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/contacts`, {
        params: { search: q, type: 'Merchant' },
        headers: getAuthHeaders(),
      });
      setResults((res.data || []).slice(0, 12));
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, [getAuthHeaders]);

  useEffect(() => {
    const t = setTimeout(() => doSearch(search), 300);
    return () => clearTimeout(t);
  }, [search, doSearch]);

  const fetchPrefillAndSelect = async (contact) => {
    try {
      const res = await axios.get(`${API}/uw/contacts/prefill/${contact.id}`, {
        headers: getAuthHeaders(),
      });
      onContactSelect(contact.id, res.data, contact);
    } catch {
      // Fallback: map basic fields from search result
      const fallback = {
        company_name: contact.company_name || '',
        owner_name:   contact.name || '',
        phone:        contact.phone || '',
        email:        contact.email || '',
        address:      contact.business_address || '',
        city:         contact.business_city || '',
        state:        contact.business_state || '',
        zip:          contact.business_zip || '',
        ein:          contact.ein || '',
        dba:          contact.dba || '',
      };
      onContactSelect(contact.id, fallback, contact);
    }
  };

  const handleSelectContact = async (contact) => {
    setSelectedContact(contact);
    setSearch('');
    setResults([]);
    setShowCreate(false);
    await fetchPrefillAndSelect(contact);

    // Load deals
    setLoadingDeals(true);
    try {
      const res = await axios.get(`${API}/deals`, {
        params: { company_id: contact.id, limit: 50 },
        headers: getAuthHeaders(),
      });
      const active = (res.data || []).filter(d =>
        !['Paid in Full','Paid in Full (Refinanced)','Closed','Closed Out','Closed - Loss',
          'Closed - Settled','Written Off','Cancelled','Declined'].includes(d.status)
      );
      setContactDeals(active);
    } catch { setContactDeals([]); }
    finally { setLoadingDeals(false); }
  };

  const handleSelectDeal = (deal) => {
    setSelectedDeal(deal);
    onDealSelect(deal.deal_id?.toString() || deal.id);
  };

  const handleClearContact = () => {
    setSelectedContact(null);
    setSelectedDeal(null);
    setContactDeals([]);
    setSearch('');
    setResults([]);
    onContactSelect('', null, null);
    onDealSelect('');
  };

  const handleClearDeal = () => {
    setSelectedDeal(null);
    onDealSelect('');
  };

  const handleCreateContact = async () => {
    if (!createForm.company_name.trim()) {
      toast.error('Business name is required');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/uw/contacts/quick-create`, createForm, {
        headers: getAuthHeaders(),
      });
      const newContact = res.data;
      toast.success(`Contact "${newContact.company_name}" created`);
      setShowCreate(false);
      setCreateForm(CREATE_FORM_DEFAULTS);
      await handleSelectContact(newContact);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create contact');
    } finally { setCreating(false); }
  };

  const cfSet = (field, val) => setCreateForm(prev => ({ ...prev, [field]: val }));

  return (
    <div className="max-w-lg mx-auto space-y-4">

      {/* ── Contact Search ── */}
      {!selectedContact && (
        <div>
          <Label className="mb-1 block">Search for a Business / Merchant</Label>
          <div className="relative">
            <Input
              value={search}
              onChange={e => { setSearch(e.target.value); setShowCreate(false); }}
              placeholder="Type company name, owner name, or DBA…"
              data-testid="contact-search-input"
            />
            {searching && (
              <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-2.5 text-gray-400" />
            )}
          </div>

          {/* Results */}
          {results.length > 0 && (
            <div className="border rounded-md mt-1 max-h-64 overflow-y-auto shadow-sm">
              {results.map(c => (
                <div
                  key={c.id}
                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b last:border-0"
                  onClick={() => handleSelectContact(c)}
                  data-testid={`contact-result-${c.shortcode || c.id}`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-sm">{c.company_name}</span>
                    {c.shortcode && <span className="text-xs text-gray-400">[{c.shortcode}]</span>}
                  </div>
                  {c.name && <p className="text-xs text-gray-500">{c.name}</p>}
                </div>
              ))}
              {/* Create on the fly row */}
              {search.trim().length >= 2 && (
                <div
                  className="px-3 py-2 hover:bg-green-50 cursor-pointer text-green-700 font-medium text-sm flex items-center gap-2 border-t"
                  onClick={() => { setShowCreate(true); cfSet('company_name', search.trim()); setResults([]); }}
                  data-testid="create-contact-from-results"
                >
                  <Plus className="h-3.5 w-3.5" /> Add "{search.trim()}" as new contact
                </div>
              )}
            </div>
          )}

          {/* No results — Create option */}
          {!searching && search.length >= 2 && results.length === 0 && !showCreate && (
            <div
              className="mt-2 px-3 py-2 border border-dashed border-green-400 rounded-md text-green-700 text-sm cursor-pointer hover:bg-green-50 flex items-center gap-2"
              onClick={() => { setShowCreate(true); cfSet('company_name', search.trim()); }}
              data-testid="create-contact-no-results"
            >
              <Plus className="h-3.5 w-3.5" /> Add "{search.trim()}" as new contact
            </div>
          )}
        </div>
      )}

      {/* ── Inline Create Form ── */}
      {showCreate && !selectedContact && (
        <Card className="border-green-300">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="h-4 w-4 text-green-600" /> New Contact
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-xs">Business Name <span className="text-red-400">*</span></Label>
                <Input value={createForm.company_name} onChange={e => cfSet('company_name', e.target.value)} placeholder="Legal business name" className="h-8 text-sm" data-testid="create-company-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Owner First Name</Label>
                  <Input value={createForm.name?.split(' ')[0] || ''} onChange={e => {
                    const parts = createForm.name?.split(' ') || ['', ''];
                    parts[0] = e.target.value;
                    cfSet('name', parts.join(' ').trim());
                  }} placeholder="First" className="h-8 text-sm" data-testid="create-owner-first" />
                </div>
                <div>
                  <Label className="text-xs">Owner Last Name</Label>
                  <Input value={createForm.name?.split(' ').slice(1).join(' ') || ''} onChange={e => {
                    const parts = createForm.name?.split(' ') || [''];
                    cfSet('name', `${parts[0]} ${e.target.value}`.trim());
                  }} placeholder="Last" className="h-8 text-sm" data-testid="create-owner-last" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input value={createForm.phone} onChange={e => cfSet('phone', e.target.value)} placeholder="555-555-5555" className="h-8 text-sm" data-testid="create-phone" />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input value={createForm.email} onChange={e => cfSet('email', e.target.value)} placeholder="owner@biz.com" className="h-8 text-sm" data-testid="create-email" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Address</Label>
                <Input value={createForm.business_address} onChange={e => cfSet('business_address', e.target.value)} placeholder="Street address" className="h-8 text-sm" data-testid="create-address" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">City</Label>
                  <Input value={createForm.city} onChange={e => cfSet('city', e.target.value)} placeholder="City" className="h-8 text-sm" data-testid="create-city" />
                </div>
                <div>
                  <Label className="text-xs">State</Label>
                  <Input value={createForm.state} onChange={e => cfSet('state', e.target.value)} placeholder="NY" maxLength={2} className="h-8 text-sm uppercase" data-testid="create-state" />
                </div>
                <div>
                  <Label className="text-xs">ZIP</Label>
                  <Input value={createForm.zip} onChange={e => cfSet('zip', e.target.value)} placeholder="10001" className="h-8 text-sm" data-testid="create-zip" />
                </div>
              </div>
              <div>
                <Label className="text-xs">EIN</Label>
                <Input value={createForm.ein} onChange={e => cfSet('ein', e.target.value)} placeholder="XX-XXXXXXX" className="h-8 text-sm" data-testid="create-ein" />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={handleCreateContact} disabled={creating || !createForm.company_name.trim()} data-testid="create-contact-save-btn">
                {creating ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />Saving…</> : 'Save & Select'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowCreate(false)} data-testid="create-contact-cancel-btn">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Selected Contact Card ── */}
      {selectedContact && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-semibold text-base">{selectedContact.company_name}</h4>
                {selectedContact.name && <p className="text-sm text-gray-600">{selectedContact.name}</p>}
                <div className="flex gap-3 mt-1 text-xs text-gray-500">
                  {selectedContact.phone && <span>{selectedContact.phone}</span>}
                  {selectedContact.email && <span>{selectedContact.email}</span>}
                  {selectedContact.shortcode && <span className="font-mono">[{selectedContact.shortcode}]</span>}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={handleClearContact} data-testid="clear-contact-btn">
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Optional: Deal Picker ── */}
      {selectedContact && (
        <div>
          <Label className="text-sm font-medium text-gray-600">
            Associate a Deal <span className="text-xs text-gray-400 font-normal">(optional — required for financial auto-fill)</span>
          </Label>

          {loadingDeals ? (
            <p className="text-sm text-gray-400 mt-1"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" />Loading deals…</p>
          ) : contactDeals.length > 0 ? (
            <div className="border rounded-md mt-1 max-h-48 overflow-y-auto">
              {contactDeals.map(d => (
                <div
                  key={d.id || d.deal_id}
                  className={`px-3 py-2 cursor-pointer border-b last:border-0 flex justify-between items-center transition-colors ${selectedDeal?.deal_id === d.deal_id ? 'bg-blue-100 border-l-2 border-l-blue-500' : 'hover:bg-gray-50'}`}
                  onClick={() => handleSelectDeal(d)}
                  data-testid={`deal-option-${d.deal_id}`}
                >
                  <div>
                    <span className="font-semibold text-sm">#{d.deal_id}</span>
                    <span className="text-sm text-gray-600 ml-2">{d.deal_name || ''}</span>
                  </div>
                  <div className="text-xs text-gray-500">{d.status}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 mt-1 italic">No active deals found — financial fields can be entered manually in the wizard.</p>
          )}

          {selectedDeal && (
            <div className="mt-2 flex items-center gap-2 bg-blue-100 rounded px-3 py-1.5 text-sm">
              <CheckSquare className="h-3.5 w-3.5 text-blue-600" />
              <span>Deal <strong>#{selectedDeal.deal_id}</strong> — {selectedDeal.deal_name}</span>
              <Button variant="ghost" size="sm" className="ml-auto h-6 px-1" onClick={handleClearDeal} data-testid="clear-deal-btn">
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};



// ─── PLACEHOLDER SECTION MAPPING ─────────────────────────────────────────────
const WIZARD_SECTIONS = {
  business: {
    label: 'Business Information',
    fields: [
      { key: 'BUSINESS_NAME', label: 'Business Name' },
      { key: 'DBA', label: 'DBA' },
      { key: 'EIN', label: 'EIN' },
      { key: 'BIZ_ADDRESS', label: 'Business Address' },
      { key: 'BUSINESS_ADDRESS', label: 'Business Address (Alt)' },
      { key: 'BIZ_CITY', label: 'City' },
      { key: 'BIZ_STATE', label: 'State' },
      { key: 'BIZ_ZIP', label: 'ZIP' },
      { key: 'EMAIL', label: 'Email' },
      { key: 'PHONE', label: 'Phone' },
      { key: 'ZIP', label: 'ZIP' },
    ],
  },
  primary_owner: {
    label: 'Primary Owner',
    fields: [
      { key: 'MERCHANT_NAME', label: 'Owner Name' },
      { key: 'SS', label: 'SSN' },
      { key: 'SSN', label: 'SSN (Alt)' },
      { key: 'M_ADD', label: 'Address' },
      { key: 'M_CIT', label: 'City' },
      { key: 'M_ST', label: 'State' },
      { key: 'M_ZIP', label: 'ZIP' },
    ],
  },
  second_owner: {
    label: 'Second Owner',
    fields: [
      { key: 'MERCHANT2_NAME', label: 'Owner 2 Name' },
      { key: 'PHONE2', label: 'Phone 2' },
      { key: 'EMAIL2', label: 'Email 2' },
      { key: 'SS2', label: 'SSN 2' },
      { key: 'M2_ADD', label: 'Address 2' },
      { key: 'M2_CIT', label: 'City 2' },
      { key: 'M2_ZIP', label: 'ZIP 2' },
    ],
  },
  financial: {
    label: 'Financial Terms',
    fields: [
      { key: 'FUND', label: 'Funding Amount' },
      { key: 'FEES', label: 'Fees' },
      { key: 'NET', label: 'Net Funding' },
      { key: 'PB', label: 'Payback' },
      { key: 'POI', label: 'Percent of Income' },
      { key: 'DP', label: 'Daily Payment' },
      { key: 'PF', label: 'Payment Frequency' },
      { key: 'WEEKDAY', label: 'Weekday' },
      { key: 'NET2', label: 'Net 2' },
      { key: 'DATE', label: 'Date' },
    ],
  },
  ach: {
    label: 'ACH / Banking',
    fields: [
      { key: 'BANK_NAME', label: 'Bank Name' },
      { key: 'ROUTING_NUMBER', label: 'Routing Number' },
      { key: 'ACCOUNT_NUMBER', label: 'Account Number' },
      { key: 'ACCOUNT_TYPE', label: 'Account Type' },
      { key: 'BANKING_DEPOSIT_PERCENT', label: 'Deposit %' },
    ],
  },
  broker_payoff: {
    label: 'Broker & Payoff',
    fields: [
      { key: 'BROKER_COMPENSATION', label: 'Broker Compensation' },
      { key: 'PAYOFF_COMPANY', label: 'Payoff Company' },
    ],
  },
};

// Derived/read-only placeholders (shown in review, not editable)
const DERIVED_KEYS = new Set([
  'DAILY_PAYMENT', 'EST_TERM_MONTHS', 'AVG_MONTHLY_INCOME', 'BALANCE',
  'TOTAL_PAYBACK', 'TOTAL_PAYMENT_AMOUNT', 'FINANCE_CHARGE', 'APR',
  'EST_MONTHLY_COST', 'NET_FUNDS', 'FACTOR_RATE', 'TERM_DAYS', 'DEAL_ID',
  'ADVANCE_AMOUNT', 'ADVANCE_DATE', 'FUNDING_DATE', 'FUNDING_PROVIDED',
  'PERCENT_OF_INCOME', 'BROKER_COMPENSATION', 'PREPAYMENT_AMOUNT',
  'PAYOFF_AMOUNT', 'PAYOFF_COMPANY', 'PAYOFF_DEAL_DATE',
  'EPO_BALANCE', 'EPO_DOLLAR_1', 'EPO_DOLLAR_2', 'EPO_DOLLAR_3',
]);

// Get all keys in a section
const getSectionKeys = (sectionId) => WIZARD_SECTIONS[sectionId]?.fields.map(f => f.key) || [];

// Determine which sections are needed for a set of placeholders
const getRequiredSections = (placeholders) => {
  const phSet = new Set(placeholders);
  const sections = [];
  for (const [id, section] of Object.entries(WIZARD_SECTIONS)) {
    const needed = section.fields.filter(f => phSet.has(f.key));
    if (needed.length > 0) sections.push({ id, ...section, neededFields: needed });
  }
  return sections;
};

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

// ─── NEW STEP: TEMPLATE SELECTION (moved to step 3) ──────────────────────────

const StepTemplateSelect = ({ company, tile, selected, onChange, getAuthHeaders, templates, setTemplates }) => {
  const [loading, setLoading] = useState(true);
  const [userPrefs, setUserPrefs] = useState({ favorites: [], recent: [] });
  const [togglingFav, setTogglingFav] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [tRes, pRes] = await Promise.all([
          axios.get(`${API}/uw/templates?company=${company}&tile=${tile}`, { headers: getAuthHeaders() }),
          axios.get(`${API}/uw/user-preferences`, { headers: getAuthHeaders() }),
        ]);
        setTemplates(tRes.data.filter(t => t.active));
        setUserPrefs(pRes.data || { favorites: [], recent: [] });
      } catch { toast.error('Failed to load templates'); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [company, tile, getAuthHeaders, setTemplates]);

  const toggle = (id) => {
    onChange(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleFavorite = async (e, tplId) => {
    e.stopPropagation();
    setTogglingFav(tplId);
    try {
      const res = await axios.post(`${API}/uw/templates/${tplId}/toggle-favorite`, {}, { headers: getAuthHeaders() });
      setUserPrefs(prev => ({ ...prev, favorites: res.data.favorites }));
    } catch { toast.error('Failed to update favorite'); }
    finally { setTogglingFav(null); }
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;
  if (templates.length === 0) return (
    <div className="text-center py-12 text-gray-500">
      <FileText className="h-10 w-10 mx-auto mb-3 text-gray-300" />
      <p>No active templates for {company} / {tile === 'contracts' ? 'Contracts' : 'ZBL'}</p>
    </div>
  );

  // Sort: favorites first → recent → others (all alphabetically within groups)
  const favSet = new Set(userPrefs.favorites || []);
  const recentSet = new Set(userPrefs.recent || []);
  const sorted = [...templates].sort((a, b) => {
    const aFav = favSet.has(a.id) ? 0 : recentSet.has(a.id) ? 1 : 2;
    const bFav = favSet.has(b.id) ? 0 : recentSet.has(b.id) ? 1 : 2;
    if (aFav !== bFav) return aFav - bFav;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="max-w-lg mx-auto space-y-2">
      <p className="text-sm text-gray-500 mb-3">Select templates to generate. Star to favorite. The questionnaire adapts to what's needed.</p>
      {sorted.map(tpl => {
        const isFav = favSet.has(tpl.id);
        const isRecent = recentSet.has(tpl.id);
        return (
          <div
            key={tpl.id}
            className={`border rounded-lg p-3 cursor-pointer transition-all flex items-center gap-3 ${selected.includes(tpl.id) ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-300' : 'hover:bg-gray-50'}`}
            onClick={() => toggle(tpl.id)}
            data-testid={`template-select-${tpl.id}`}
          >
            <CheckSquare className={`h-5 w-5 flex-shrink-0 ${selected.includes(tpl.id) ? 'text-blue-600' : 'text-gray-300'}`} />
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm flex items-center gap-2 flex-wrap">
                {tpl.name}
                <Badge variant="outline" className="text-[10px] px-1.5">
                  {tpl.template_type === 'pdf_acroform' ? 'PDF' : 'Doc'}
                </Badge>
                {isFav && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                    <Star className="h-2.5 w-2.5 fill-amber-500 text-amber-500" /> Favorite
                  </span>
                )}
                {!isFav && isRecent && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-medium">
                    <Clock className="h-2.5 w-2.5" /> Recent
                  </span>
                )}
              </h4>
              <p className="text-xs text-gray-400">{(tpl.placeholders || []).length} placeholders</p>
            </div>
            <button
              className={`p-1.5 rounded-full transition-colors flex-shrink-0 ${isFav ? 'text-amber-500 hover:bg-amber-100' : 'text-gray-300 hover:text-amber-400 hover:bg-amber-50'}`}
              onClick={(e) => toggleFavorite(e, tpl.id)}
              disabled={togglingFav === tpl.id}
              title={isFav ? 'Remove from favorites' : 'Add to favorites'}
              data-testid={`fav-btn-${tpl.id}`}
            >
              {togglingFav === tpl.id
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Star className={`h-4 w-4 ${isFav ? 'fill-amber-500' : ''}`} />
              }
            </button>
          </div>
        );
      })}
      <p className="text-xs text-gray-500 text-center mt-3">{selected.length} template(s) selected</p>
    </div>
  );
};

// ─── NEW STEP: WIZARD QUESTIONNAIRE ──────────────────────────────────────────

const StepWizard = ({
  dealId, contactPrefill, company, templates, selectedTemplates,
  wizardData, setWizardData,
  hasSecondOwner, setHasSecondOwner,
  getAuthHeaders,
}) => {
  const [loading, setLoading] = useState(true);
  const [prefilled, setPrefilled] = useState(false);
  const [contactPrefilled, setContactPrefilled] = useState(false);

  // Compute the placeholder union from selected templates
  const allPlaceholders = useMemo(() => {
    const phSet = new Set();
    templates.filter(t => selectedTemplates.includes(t.id)).forEach(t => {
      (t.placeholders || []).forEach(p => phSet.add(p));
    });
    return [...phSet];
  }, [templates, selectedTemplates]);

  const requiredSections = useMemo(() => getRequiredSections(allPlaceholders), [allPlaceholders]);
  const hasSection = (id) => requiredSections.some(s => s.id === id);

  // Prefill from deal
  useEffect(() => {
    if (!dealId || prefilled) { setLoading(false); return; }
    const fetchPrefill = async () => {
      try {
        const res = await axios.get(`${API}/uw/deal-prefill/${dealId}`, { headers: getAuthHeaders() });
        const p = res.data;
        setWizardData(prev => ({
          ...prev,
          BUSINESS_NAME: prev.BUSINESS_NAME || p.merchant_name || '',
          MERCHANT_NAME: prev.MERCHANT_NAME || p.contact_name || '',
          EMAIL: prev.EMAIL || p.email || '',
          PHONE: prev.PHONE || p.phone || '',
          EIN: prev.EIN || p.ein || '',
          DBA: prev.DBA || p.dba || '',
          BIZ_ADDRESS: prev.BIZ_ADDRESS || p.address || '',
          BUSINESS_ADDRESS: prev.BUSINESS_ADDRESS || p.address || '',
          M_ADD: prev.M_ADD || '',
          // Financial prefill
          FUND: prev.FUND || (p.advance ? String(p.advance) : ''),
          DATE: prev.DATE || p.funding_date || new Date().toISOString().slice(0, 10),
          // Intake-style prefill
          _advance: prev._advance || p.advance || '',
          _factor: prev._factor || p.factor || '',
          _term: prev._term || p.term || '',
          _paymentFrequency: prev._paymentFrequency || p.payment_frequency || 'Daily',
          _originationFee: prev._originationFee || p.origination_fee || 0,
          _brokerFee: prev._brokerFee || p.broker_fee || 0,
          _fundingDate: prev._fundingDate || p.funding_date || new Date().toISOString().slice(0, 10),
        }));
        setPrefilled(true);
      } catch (e) { console.error('Template fetch failed:', e); }
      finally { setLoading(false); }
    };
    fetchPrefill();
  }, [dealId, getAuthHeaders, prefilled, setWizardData]);

  // Contact prefill — applied first so deal prefill can selectively override financial fields
  useEffect(() => {
    if (!contactPrefill || contactPrefilled) return;
    const mapped = mapContactToWizard(contactPrefill);
    setWizardData(prev => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(mapped)) {
        if (!next[k] && v) next[k] = v;   // only fill if not already set
      }
      return next;
    });
    setContactPrefilled(true);
  }, [contactPrefill, contactPrefilled, setWizardData]);

  const set = (key, val) => setWizardData(prev => ({ ...prev, [key]: val }));

  // Payment frequency logic
  const pf = wizardData.PF || wizardData._paymentFrequency || 'Daily';
  const setPF = (val) => {
    set('PF', val);
    set('_paymentFrequency', val);
    if (val === 'Daily') set('WEEKDAY', '');
  };

  // Weekly payment auto-fill: when PF=Weekly, DP = base daily payment × 5
  useEffect(() => {
    if (pf !== 'Weekly') return;
    const adv = Number(wizardData._advance);
    const fac = Number(wizardData._factor);
    const trm = Number(wizardData._term);
    if (adv > 0 && fac >= 1 && trm > 0) {
      const weeklyPayment = ((adv * fac) / trm) * 5;
      set('DP', weeklyPayment.toFixed(2));
    }
  }, [pf, wizardData._advance, wizardData._factor, wizardData._term]);

  // Live calculations
  const calc = useMemo(() => {
    const adv = Number(wizardData._advance);
    const fac = Number(wizardData._factor);
    const trm = Number(wizardData._term);
    if (adv > 0 && fac >= 1 && trm > 0) {
      return calculateIntake({
        advance: adv, factor: fac, term: trm,
        paymentFrequency: pf,
        originationFee: Number(wizardData._originationFee) || 0,
        brokerFee: Number(wizardData._brokerFee) || 0,
        otherFees: 0,
        fundingDate: wizardData._fundingDate || wizardData.DATE,
      });
    }
    return null;
  }, [wizardData, pf]);

  // Deposit-derived calculation — shown when templates require AVG_MONTHLY_INCOME or PERCENT_OF_INCOME
  const needsDepositSection = allPlaceholders.includes('AVG_MONTHLY_INCOME') ||
                              allPlaceholders.includes('PERCENT_OF_INCOME');

  const depositCalc = useMemo(() => {
    if (!needsDepositSection) return null;
    const deps = [
      Number(wizardData._dep1),
      Number(wizardData._dep2),
      Number(wizardData._dep3),
      Number(wizardData._dep4),
    ];
    if (!deps.every(d => d > 0)) return null;
    const adv = Number(wizardData._advance);
    const fac = Number(wizardData._factor);
    const trm = Number(wizardData._term);
    if (!(adv > 0 && fac >= 1 && trm > 0)) return null;
    const avg = deps.reduce((s, d) => s + d, 0) / 4;
    // Use base daily-equivalent payment (advance × factor ÷ term) so Daily and Weekly
    // frequencies produce distinct POI values per the spec: Daily=DP×20, Weekly=DP×4
    const dp = (adv * fac) / trm;
    const monthlyPayment = pf === 'Weekly' ? dp * 4 : dp * 20;
    const poi = avg > 0 ? (monthlyPayment / avg) * 100 : 0;
    return {
      avgFormatted: `$${avg.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      poiFormatted: `${poi.toFixed(2)}%`,
    };
  }, [wizardData._dep1, wizardData._dep2, wizardData._dep3, wizardData._dep4, wizardData._advance, wizardData._factor, wizardData._term, pf, needsDepositSection]);

  // POI auto-fill: whenever deposit calc is available, pre-fill POI field (user can override)
  useEffect(() => {
    if (depositCalc?.poiFormatted) {
      set('POI', depositCalc.poiFormatted);
    }
  }, [depositCalc?.poiFormatted]);

  // Second owner validation
  const secondOwnerKeys = getSectionKeys('second_owner');
  const needsSecondOwner = secondOwnerKeys.some(k => allPlaceholders.includes(k));

  if (loading) return <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Loading deal data...</div>;

  const numVal = (v) => v === '' || v === undefined || v === null ? '' : v;

  const renderField = (key, label, opts = {}) => {
    const isNum = opts.type === 'number';
    return (
      <div key={key}>
        <Label className="text-xs">{label} <span className="text-red-400">*</span></Label>
        <Input
          type={opts.type || 'text'}
          step={opts.step}
          value={isNum ? numVal(wizardData[key]) : (wizardData[key] || '')}
          onChange={e => set(key, isNum ? (e.target.value === '' ? '' : parseFloat(e.target.value)) : e.target.value)}
          placeholder={opts.placeholder || ''}
          className="h-8 text-sm"
          data-testid={`wiz-${key}`}
        />
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* Context bar */}
      <div className="flex items-center gap-3 bg-slate-50 rounded-lg px-4 py-2.5 text-sm">
        <Calculator className="h-4 w-4 text-blue-600" />
        {contactPrefill?.company_name
          ? <span className="font-medium">{contactPrefill.company_name}</span>
          : dealId
            ? <span className="font-medium">Deal #{dealId}</span>
            : <span className="text-gray-500 italic">No contact/deal selected</span>
        }
        {dealId && contactPrefill?.company_name && (
          <><span className="text-gray-400">|</span><span className="text-gray-500 text-xs">Deal #{dealId}</span></>
        )}
        <span className="text-gray-400">|</span>
        <span className="text-gray-500">{selectedTemplates.length} template(s) → {allPlaceholders.length} placeholders</span>
      </div>

      {/* SECTION: Business Info */}
      {hasSection('business') && (
        <Card data-testid="wiz-section-business">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-blue-600" /> Business Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {requiredSections.find(s => s.id === 'business').neededFields.map(f => renderField(f.key, f.label))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SECTION: Primary Owner */}
      {hasSection('primary_owner') && (
        <Card data-testid="wiz-section-primary-owner">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Primary Owner</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {requiredSections.find(s => s.id === 'primary_owner').neededFields.map(f => renderField(f.key, f.label))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SECTION: Second Owner (Conditional) */}
      {needsSecondOwner && (
        <Card data-testid="wiz-section-second-owner">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-3">
              Second Owner
              <div className="flex items-center gap-2 ml-auto">
                <input
                  type="checkbox" id="has_second_owner"
                  checked={hasSecondOwner}
                  onChange={e => setHasSecondOwner(e.target.checked)}
                  className="h-4 w-4 rounded"
                />
                <Label htmlFor="has_second_owner" className="text-xs cursor-pointer">This deal has a second owner</Label>
              </div>
            </CardTitle>
          </CardHeader>
          {hasSecondOwner && (
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {requiredSections.find(s => s.id === 'second_owner')?.neededFields.map(f => renderField(f.key, f.label))}
              </div>
            </CardContent>
          )}
          {!hasSecondOwner && (
            <CardContent>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                Template requires second owner fields. Check the box above or generation will be blocked.
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* SECTION: Financial Terms — shown when template has financial fields OR deposit-derived fields */}
      {(hasSection('financial') || needsDepositSection) && (
        <Card data-testid="wiz-section-financial">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Financial Terms</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Core deal terms for calculation engine */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Advance Amount <span className="text-red-400">*</span></Label>
                <Input type="number" step="0.01" min="0" value={numVal(wizardData._advance)} onChange={e => set('_advance', e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-8 text-sm" data-testid="wiz-advance" />
              </div>
              <div>
                <Label className="text-xs">Factor Rate <span className="text-red-400">*</span></Label>
                <Input type="number" step="0.001" min="1" value={numVal(wizardData._factor)} onChange={e => set('_factor', e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-8 text-sm" data-testid="wiz-factor" />
              </div>
              <div>
                <Label className="text-xs">Term (business days) <span className="text-red-400">*</span></Label>
                <Input type="number" step="1" min="1" value={numVal(wizardData._term)} onChange={e => set('_term', e.target.value === '' ? '' : parseInt(e.target.value, 10))} className="h-8 text-sm" data-testid="wiz-term" />
              </div>
            </div>

            {/* Payment Frequency */}
            <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-testid="wiz-pf-section">
              <Label className="text-xs font-semibold">Payment Frequency <span className="text-red-400">*</span></Label>
              <div className="flex gap-4">
                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all ${pf === 'Daily' ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-300' : 'bg-white hover:bg-gray-50'}`}>
                  <input type="radio" name="pf" value="Daily" checked={pf === 'Daily'} onChange={() => setPF('Daily')} className="h-4 w-4" data-testid="wiz-pf-daily" />
                  <span className="text-sm font-medium">Daily (Mon–Fri)</span>
                </label>
                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all ${pf === 'Weekly' ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-300' : 'bg-white hover:bg-gray-50'}`}>
                  <input type="radio" name="pf" value="Weekly" checked={pf === 'Weekly'} onChange={() => setPF('Weekly')} className="h-4 w-4" data-testid="wiz-pf-weekly" />
                  <span className="text-sm font-medium">Weekly</span>
                </label>
              </div>
              {pf === 'Weekly' && (
                <div className="mt-2">
                  <Label className="text-xs">Weekday <span className="text-red-400">*</span></Label>
                  <Select value={wizardData.WEEKDAY || ''} onValueChange={v => set('WEEKDAY', v)}>
                    <SelectTrigger className="h-8 w-48" data-testid="wiz-weekday"><SelectValue placeholder="Select day" /></SelectTrigger>
                    <SelectContent>
                      {WEEKDAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Other financial fields */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Origination Fee</Label>
                <Input type="number" step="0.01" value={numVal(wizardData._originationFee)} onChange={e => set('_originationFee', e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-8 text-sm" data-testid="wiz-orig-fee" />
              </div>
              <div>
                <Label className="text-xs">Broker Fee</Label>
                <Input type="number" step="0.01" value={numVal(wizardData._brokerFee)} onChange={e => set('_brokerFee', e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-8 text-sm" data-testid="wiz-broker-fee" />
              </div>
              <div>
                <Label className="text-xs">Funding Date</Label>
                <Input type="date" value={wizardData._fundingDate || wizardData.DATE || ''} onChange={e => { set('_fundingDate', e.target.value); set('DATE', e.target.value); }} className="h-8 text-sm" data-testid="wiz-date" />
              </div>
            </div>

            {/* Calculated Outputs (live preview) */}
            {calc && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                <p className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1">
                  <Calculator className="h-3 w-3" /> Live Calculated Values
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    ['Payback', formatCurrency(calc.payback)],
                    ['Payment Amt', formatCurrency(calc.paymentAmount)],
                    ['Net Funding', formatCurrency(calc.netFunding)],
                    ['Finance Charge', formatCurrency(calc.financeCharge)],
                    ['Est. Monthly', formatCurrency(calc.monthlyCostEst)],
                    ['APR Est.', `${calc.aprEstimate}%`],
                    ['Maturity', calc.maturityDateEst || '—'],
                    ['Payments', calc.paymentCount],
                  ].map(([label, val]) => (
                    <div key={label} className="bg-white rounded px-2 py-1.5 border text-xs">
                      <span className="text-gray-500">{label}:</span>
                      <span className="font-semibold ml-1">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Deposit History — only shown when template requires AVG_MONTHLY_INCOME or PERCENT_OF_INCOME */}
            {needsDepositSection && (
              <div className="border-t pt-4 mt-3" data-testid="wiz-deposit-section">
                <p className="text-xs font-semibold text-gray-700 mb-2">
                  Deposit History <span className="text-gray-400 font-normal">(last 4 months)</span> <span className="text-red-400">*</span>
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i}>
                      <Label className="text-xs text-gray-500">Month {i}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={numVal(wizardData[`_dep${i}`])}
                        onChange={e => set(`_dep${i}`, e.target.value === '' ? '' : parseFloat(e.target.value))}
                        placeholder="e.g. 15000"
                        className="h-8 text-sm"
                        data-testid={`wiz-dep${i}`}
                      />
                    </div>
                  ))}
                </div>
                {depositCalc ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white rounded px-2 py-1.5 border text-xs">
                        <span className="text-gray-500">Avg Monthly Income:</span>
                        <span className="font-semibold ml-1 text-emerald-700" data-testid="wiz-avg-monthly">{depositCalc.avgFormatted}</span>
                      </div>
                      <div className="bg-white rounded px-2 py-1.5 border text-xs">
                        <span className="text-gray-500">% of Income:</span>
                        <span className="font-semibold ml-1 text-emerald-700" data-testid="wiz-pct-income">{depositCalc.poiFormatted}</span>
                      </div>
                    </div>
                    <p className="text-[10px] text-emerald-600 mt-1.5">These values will be submitted automatically — no manual entry required.</p>
                  </div>
                ) : (
                  (wizardData._dep1 || wizardData._dep2 || wizardData._dep3 || wizardData._dep4) && (
                    <p className="text-xs text-amber-600">Enter all 4 months (and advance/factor/term) to compute averages.</p>
                  )
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* SECTION: ACH / Banking */}
      {hasSection('ach') && (
        <Card data-testid="wiz-section-ach">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">ACH / Banking</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {requiredSections.find(s => s.id === 'ach').neededFields.map(f => renderField(f.key, f.label))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SECTION: Broker & Payoff — conditional on template placeholders */}
      {hasSection('broker_payoff') && (
        <Card data-testid="wiz-section-broker-payoff">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Broker &amp; Payoff</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {requiredSections.find(s => s.id === 'broker_payoff').neededFields.map(f => renderField(f.key, f.label))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <div className="bg-slate-50 rounded-lg p-3 text-xs text-gray-500">
        Sections shown: {requiredSections.map(s => s.label).join(' → ')}
      </div>
    </div>
  );
};


// ─── NEW STEP REVIEW & GENERATE ──────────────────────────────────────────────

const SOURCE_COLORS = {
  manual: 'bg-purple-100 text-purple-700',
  intake: 'bg-amber-100 text-amber-700',
  calculated: 'bg-blue-100 text-blue-700',
  deal: 'bg-emerald-100 text-emerald-700',
  contact: 'bg-teal-100 text-teal-700',
  contact_sensitive: 'bg-red-100 text-red-700',
  auto: 'bg-gray-100 text-gray-600',
  blocked: 'bg-red-200 text-red-800',
  derived: 'bg-indigo-100 text-indigo-700',
  constant: 'bg-slate-100 text-slate-600',
  missing:  'bg-red-100 text-red-600',
};

const SourceBadge = ({ source }) => (
  <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-medium ${SOURCE_COLORS[source] || 'bg-gray-100 text-gray-500'}`}>
    {source}
  </span>
);

// ─── REVIEW GROUP CLASSIFICATION ─────────────────────────────────────────────

const REVIEW_GROUP_KEYS = {
  Business:  new Set(['BUSINESS_NAME', 'DBA', 'EIN', 'BIZ_ADDRESS', 'BIZ_CITY', 'BIZ_STATE', 'BIZ_ZIP', 'CORP_TYPE', 'START_DATE', 'LEGAL_STATE', 'LEGAL_ENTITY', 'SHORTCODE', 'DEAL_ID', 'DEAL_ID_NUM']),
  Owner:     new Set(['MERCHANT_NAME', 'SS', 'SSN', 'M_ADD', 'M_CIT', 'M_ZIP', 'PHONE', 'EMAIL', 'DOB', 'M_OWN', 'M_TIT', 'MERCHANT2_NAME', 'SS2', 'PHONE2', 'EMAIL2', 'M2_ADD', 'M2_CIT', 'M2_ZIP', 'DOB2', 'M_OWN2', 'M_TIT2']),
  Entity:    new Set(['LEGAL_NAME', 'OFFICER']),
  Financial: new Set(['FUND', 'FEES', 'NET', 'PB', 'POI', 'DP', 'PF', 'WEEKDAY', 'NET2', 'DATE', 'ORIGINATION_FEE', 'BROKER_FEE', 'BROKER_COMPENSATION', 'ADVANCE_AMOUNT', 'ADVANCE_DATE', 'FUNDING_DATE', 'FUNDING_PROVIDED', 'PAYOFF_COMPANY', 'PAYOFF_AMOUNT', 'PAYOFF_DEAL_DATE', 'EPO_BALANCE', 'EPO_DOLLAR_1', 'EPO_DOLLAR_2', 'EPO_DOLLAR_3', 'PREPAYMENT_AMOUNT']),
  ACH:       new Set(['BANK_NAME', 'ROUTING_NUMBER', 'ACCOUNT_NUMBER', 'ACCOUNT_TYPE', 'BANKING_DEPOSIT_PERCENT']),
  Derived:   new Set(['DAILY_PAYMENT', 'EST_TERM_MONTHS', 'AVG_MONTHLY_INCOME', 'BALANCE', 'TOTAL_PAYBACK', 'TOTAL_PAYMENT_AMOUNT', 'FINANCE_CHARGE', 'APR', 'EST_MONTHLY_COST', 'NET_FUNDS', 'FACTOR_RATE', 'TERM_DAYS', 'PERCENT_OF_INCOME', 'BALANCE_AMOUNT', 'EFFECTIVE_BALANCE', 'PAYOFF_BALANCE', 'REMAINING_BALANCE']),
};

const getReviewGroup = (key) => {
  for (const [group, keys] of Object.entries(REVIEW_GROUP_KEYS)) {
    if (keys.has(key)) return group;
  }
  return 'Other';
};

const REVIEW_GROUP_ORDER = ['Business', 'Owner', 'Entity', 'Financial', 'ACH', 'Derived', 'Other'];

const REVIEW_GROUP_STYLES = {
  Business:  { dot: 'bg-blue-500',    header: 'text-blue-700',    border: 'border-blue-200',    headerBg: 'bg-blue-50'    },
  Owner:     { dot: 'bg-violet-500',  header: 'text-violet-700',  border: 'border-violet-200',  headerBg: 'bg-violet-50'  },
  Entity:    { dot: 'bg-orange-500',  header: 'text-orange-700',  border: 'border-orange-200',  headerBg: 'bg-orange-50'  },
  Financial: { dot: 'bg-emerald-500', header: 'text-emerald-700', border: 'border-emerald-200', headerBg: 'bg-emerald-50' },
  ACH:       { dot: 'bg-cyan-500',    header: 'text-cyan-700',    border: 'border-cyan-200',    headerBg: 'bg-cyan-50'    },
  Derived:   { dot: 'bg-gray-500',    header: 'text-gray-600',    border: 'border-gray-200',    headerBg: 'bg-gray-50'    },
  Other:     { dot: 'bg-slate-400',   header: 'text-slate-600',   border: 'border-slate-200',   headerBg: 'bg-slate-50'   },
};

const ReviewRow = ({ entry, manualInputs, onEdit }) => {
  const isSystemResolved = !entry.missing;
  const hasManualValue = !!manualInputs[entry.key];
  const isBlocked = entry.reason === 'requires admin permission';
  const rowBg = (entry.missing && !hasManualValue) ? 'bg-red-50' : 'hover:bg-gray-50/60';
  return (
    <div className={`grid grid-cols-12 px-3 py-2 items-center text-xs transition-colors ${rowBg}`} data-testid={`review-row-${entry.key}`}>
      <div className="col-span-4 pr-2 min-w-0">
        <div className="text-gray-700 font-medium leading-tight truncate" title={entry.label}>{entry.label}</div>
        <div className="text-[10px] text-gray-400 font-mono truncate">{entry.key}</div>
      </div>
      <div className="col-span-4 pr-2 min-w-0">
        {isSystemResolved ? (
          <span className="text-gray-800 truncate block" title={entry.display_value || entry.value}>
            {entry.display_value || entry.value || <span className="text-gray-300 italic">—</span>}
          </span>
        ) : (
          <Input
            value={manualInputs[entry.key] || ''}
            onChange={e => onEdit(entry.key, e.target.value)}
            placeholder={isBlocked ? 'Admin required' : (entry.help_text || 'Enter value')}
            disabled={isBlocked}
            className={`h-7 text-xs py-0 ${hasManualValue ? 'border-green-300 bg-green-50/50' : 'border-red-200 bg-red-50/50'}`}
            data-testid={`missing-field-${entry.key}`}
          />
        )}
      </div>
      <div className="col-span-2">
        <SourceBadge source={isSystemResolved ? entry.source : (hasManualValue ? 'manual' : 'missing')} />
      </div>
      <div className="col-span-2 flex justify-end pr-1">
        {isSystemResolved
          ? <Lock className="h-3 w-3 text-gray-300" />
          : hasManualValue
            ? <Check className="h-3 w-3 text-green-500" />
            : <Edit className="h-3 w-3 text-red-400" />
        }
      </div>
    </div>
  );
};

const ReviewGroupTable = ({ group, entries, manualInputs, onEdit }) => {
  const styles = REVIEW_GROUP_STYLES[group] || REVIEW_GROUP_STYLES.Other;
  const [expanded, setExpanded] = useState(true);
  const missingCount = entries.filter(e => e.missing && !manualInputs[e.key]).length;
  return (
    <div className={`rounded-lg border ${styles.border} overflow-hidden`} data-testid={`review-group-${group.toLowerCase()}`}>
      <div
        className={`${styles.headerBg} border-b ${styles.border} px-3 py-2 flex items-center gap-2 cursor-pointer select-none`}
        onClick={() => setExpanded(v => !v)}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${styles.dot} flex-shrink-0`} />
        <span className={`text-xs font-semibold uppercase tracking-wide flex-1 ${styles.header}`}>{group}</span>
        <span className="text-[10px] text-gray-400">{entries.length} field{entries.length !== 1 ? 's' : ''}</span>
        {missingCount > 0 && (
          <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold ml-1">{missingCount} missing</span>
        )}
        <ChevronRight className={`h-3.5 w-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </div>
      {expanded && (
        <div>
          <div className="grid grid-cols-12 bg-gray-50 border-b border-gray-100 px-3 py-1.5 text-[10px] text-gray-400 uppercase tracking-wide font-semibold">
            <span className="col-span-4">Placeholder</span>
            <span className="col-span-4">Value</span>
            <span className="col-span-2">Source</span>
            <span className="col-span-2 text-right pr-1">Edit</span>
          </div>
          <div className="divide-y divide-gray-100">
            {entries.map(entry => (
              <ReviewRow key={`${entry.missing ? 'miss' : 'res'}-${entry.key}`} entry={entry} manualInputs={manualInputs} onEdit={onEdit} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StepReview = ({ company, tile, dealId, contactId, templateIds, manualInputs, setManualInputs, onGenerate, onSaveToDrive, generating, savingToDrive, getAuthHeaders, intakeData, hasSecondOwner, setHasSecondOwner }) => {
  const [resolvedData, setResolvedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zblWarning, setZblWarning] = useState(null);
  const [newPh, setNewPh] = useState('');

  useEffect(() => {
    const fetchResolved = async () => {
      try {
        const intakePayload = intakeData && Number(intakeData.advance) > 0 ? {
          advance: Number(intakeData.advance),
          factor: Number(intakeData.factor),
          term: Number(intakeData.term),
          payment_frequency: intakeData.paymentFrequency || 'Daily',
          origination_fee: Number(intakeData.originationFee) || 0,
          broker_fee: Number(intakeData.brokerFee) || 0,
          other_fees: Number(intakeData.otherFees) || 0,
          net_funding_override: intakeData.netFundingOverride ? Number(intakeData.netFundingOverride) : null,
          payment_count_override: intakeData.paymentCountOverride ? Number(intakeData.paymentCountOverride) : null,
          funding_date: intakeData.fundingDate || null,
          merchant_name: intakeData.merchantName || null,
          contact_name: intakeData.contactName || null,
          email: intakeData.email || null,
          phone: intakeData.phone || null,
          address: intakeData.address || null,
          ein: intakeData.ein || null,
        } : null;
        const res = await axios.post(`${API}/uw/resolve-data`, {
          company, deal_id: dealId, contact_id: contactId, template_ids: templateIds,
          intake_data: intakePayload, manual_overrides: manualInputs, has_second_owner: hasSecondOwner,
        }, { headers: getAuthHeaders() });
        setResolvedData(res.data);
        if (tile === 'zbl') setZblWarning('Effective balance will be checked at generation time.');
      } catch { toast.error('Failed to resolve template data'); }
      finally { setLoading(false); }
    };
    fetchResolved();
  }, [templateIds, company, dealId, contactId, tile, getAuthHeaders, intakeData, hasSecondOwner]);

  const handleManualChange = (key, value) => setManualInputs(prev => ({ ...prev, [key]: value }));

  const addCustomPlaceholder = () => {
    if (!newPh || newPh.length < 2) return;
    const key = newPh.toUpperCase().replace(/[^A-Z0-9_]/g, '');
    if (!key) return;
    setResolvedData(prev => {
      if (!prev) return prev;
      if (prev.resolved[key] || prev.missing_fields.some(f => f.key === key)) return prev;
      return {
        ...prev,
        missing_fields: [...prev.missing_fields, { key, label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), help_text: '', reason: '', template_ids: [], template_names: [] }],
        counts: { ...prev.counts, total_fields: prev.counts.total_fields + 1, missing: prev.counts.missing + 1 },
      };
    });
    setNewPh('');
  };

  // Build unified flat entry list — all hooks must be called before early returns
  const allEntries = useMemo(() => {
    const resolved = resolvedData?.resolved || {};
    const missingFields = resolvedData?.missing_fields || [];
    const result = [];
    const missingKeySet = new Set(missingFields.map(f => f.key));
    for (const [key, entry] of Object.entries(resolved)) {
      if (!entry.value && missingKeySet.has(key)) continue;
      result.push({ key, label: entry.label || key, value: entry.value, display_value: entry.display_value || entry.value, source: entry.source, missing: false });
    }
    for (const f of missingFields) {
      if (!resolved[f.key]?.value) {
        result.push({ key: f.key, label: f.label || f.key, value: '', source: f.source || 'missing', missing: true, help_text: f.help_text, reason: f.reason });
      }
    }
    return result;
  }, [resolvedData]);

  const grouped = useMemo(() => {
    const groups = Object.fromEntries(REVIEW_GROUP_ORDER.map(g => [g, []]));
    for (const entry of allEntries) groups[getReviewGroup(entry.key)].push(entry);
    return groups;
  }, [allEntries]);

  if (loading) return <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;

  const blockedEntries = Object.entries(resolvedData?.resolved || {}).filter(([, v]) => v.source === 'blocked');
  // allEntries is the deduplicated single source of truth — use it for all counts
  const stillMissingCount = allEntries.filter(e => e.missing && !manualInputs[e.key]).length;
  const totalPlaceholders = allEntries.length;
  const resolvedCount = allEntries.filter(e => !e.missing || !!manualInputs[e.key]).length;

  return (
    <div className="max-w-2xl mx-auto space-y-4">

      {/* ── SUMMARY BAR ── */}
      <div className="grid grid-cols-5 bg-slate-900 text-white rounded-xl overflow-hidden" data-testid="review-summary-bar">
        {[
          { label: 'Templates',    value: templateIds.length,  color: 'text-white' },
          { label: 'Total Fields', value: totalPlaceholders,   color: 'text-white' },
          { label: 'Resolved',     value: resolvedCount,       color: 'text-emerald-400' },
          { label: 'Missing',      value: stillMissingCount,   color: stillMissingCount > 0 ? 'text-red-400' : 'text-emerald-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="flex flex-col items-center justify-center py-3 border-r border-slate-700">
            <span className={`text-xl font-bold ${color}`} data-testid={`summary-${label.toLowerCase().replace(' ', '-')}`}>{value}</span>
            <span className="text-[10px] text-slate-400 uppercase tracking-wide mt-0.5">{label}</span>
          </div>
        ))}
        <div className="flex flex-col items-center justify-center py-3">
          {stillMissingCount === 0 ? (
            <>
              <Check className="h-5 w-5 text-emerald-400" />
              <span className="text-[10px] text-emerald-400 font-semibold uppercase tracking-wide mt-0.5" data-testid="summary-status">Ready</span>
            </>
          ) : (
            <>
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <span className="text-[10px] text-red-400 font-semibold uppercase tracking-wide mt-0.5" data-testid="summary-status">Blocked</span>
            </>
          )}
        </div>
      </div>

      {/* Second owner toggle */}
      <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3" data-testid="second-owner-toggle">
        <input type="checkbox" id="has_second_owner" checked={hasSecondOwner} onChange={e => setHasSecondOwner(e.target.checked)} className="h-4 w-4 rounded border-gray-300" />
        <Label htmlFor="has_second_owner" className="text-sm cursor-pointer">Second owner on this deal?</Label>
        {hasSecondOwner && <Badge className="bg-violet-100 text-violet-700 text-[10px]">M2 fields included</Badge>}
      </div>

      {/* Warnings */}
      {blockedEntries.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm" data-testid="blocked-fields-warning">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-800">Permission Required</p>
              <p className="text-red-700 text-xs">Admin access needed: {blockedEntries.map(([k]) => k).join(', ')}</p>
            </div>
          </div>
        </div>
      )}
      {zblWarning && tile === 'zbl' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-700">{zblWarning}</p>
        </div>
      )}

      {/* ── GROUPED PREVIEW TABLES ── */}
      {REVIEW_GROUP_ORDER.filter(g => (grouped[g] || []).length > 0).map(group => (
        <ReviewGroupTable key={group} group={group} entries={grouped[group]} manualInputs={manualInputs} onEdit={handleManualChange} />
      ))}

      {/* Add custom placeholder */}
      <div className="border-t pt-4">
        <Label className="text-xs text-gray-600 mb-1 block">Add placeholder on the fly</Label>
        <div className="flex items-center gap-2">
          <Input value={newPh} onChange={e => setNewPh(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))} placeholder="NEW_PLACEHOLDER" className="h-8 text-sm font-mono flex-1" data-testid="add-placeholder-on-fly-input" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomPlaceholder(); } }} />
          <Button type="button" size="sm" variant="outline" className="h-8" disabled={!newPh || newPh.length < 2} onClick={addCustomPlaceholder} data-testid="add-placeholder-on-fly-btn">
            <PlusCircle className="h-3.5 w-3.5 mr-1" />Add
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-1">Must match a {'{{PLACEHOLDER}}'} in the Google Doc or mapped PDF field</p>
      </div>

      {/* Generate / Save buttons */}
      <div className="flex gap-3">
        <Button className="flex-1" size="lg" onClick={onGenerate} disabled={generating || savingToDrive || blockedEntries.length > 0 || stillMissingCount > 0} data-testid="generate-btn">
          {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><FileDown className="h-4 w-4 mr-2" />Download PDF</>}
        </Button>
        <Button className="flex-1" size="lg" variant="secondary" onClick={onSaveToDrive} disabled={generating || savingToDrive || blockedEntries.length > 0 || stillMissingCount > 0} data-testid="save-to-drive-btn">
          {savingToDrive ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><FolderOpen className="h-4 w-4 mr-2" />Save to Drive</>}
        </Button>
      </div>
      {stillMissingCount > 0 && (
        <p className="text-xs text-red-500 text-center" data-testid="strict-validation-msg">
          All placeholders must be filled before generating. {stillMissingCount} field{stillMissingCount !== 1 ? 's' : ''} remaining.
        </p>
      )}
    </div>
  );
};

// ─── MAIN PAGE ───────────────────────────────────────────────────────────────

export default function UWToolkit() {
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = user?.roles?.includes('admin');
  
  // Check UW Toolkit access: admin always has access, OR user has feature flag enabled
  const hasUWToolkitAccess = isAdmin || user?.uw_toolkit_enabled === true;

  const [step, setStep] = useState(0);
  const [company, setCompany] = useState('');
  const [tile, setTile] = useState('');
  const [dealId, setDealId] = useState('');
  const [contactId, setContactId] = useState('');
  const [contactPrefill, setContactPrefill] = useState(null); // raw prefill from contact endpoint
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [manualInputs, setManualInputs] = useState({});
  const [generating, setGenerating] = useState(false);
  const [savingToDrive, setSavingToDrive] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [hasSecondOwner, setHasSecondOwner] = useState(false);
  const [wizardData, setWizardData] = useState({});

  // Draft state
  const [drafts, setDrafts] = useState([]);
  const [showDrafts, setShowDrafts] = useState(false);
  const [saveDraftOpen, setSaveDraftOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const fetchDrafts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/uw/drafts`, { headers: getAuthHeaders() });
      setDrafts(res.data || []);
    } catch (e) { console.error('Fetch failed:', e); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const handleSaveDraft = async () => {
    if (!draftName.trim()) return;
    setSavingDraft(true);
    try {
      const res = await axios.post(`${API}/uw/drafts`, {
        draft_id: activeDraftId || undefined,
        name: draftName.trim(),
        step,
        company, tile,
        contact_id: contactId,
        deal_id: dealId,
        selected_templates: selectedTemplates,
        wizard_data: wizardData,
        manual_inputs: manualInputs,
        has_second_owner: hasSecondOwner,
        contact_name: contactPrefill?.company_name || '',
        deal_name: dealId ? `Deal #${dealId}` : '',
      }, { headers: getAuthHeaders() });
      setActiveDraftId(res.data.id);
      toast.success(`Draft "${res.data.name}" saved`);
      setSaveDraftOpen(false);
      fetchDrafts();
    } catch { toast.error('Failed to save draft'); }
    finally { setSavingDraft(false); }
  };

  const handleLoadDraft = async (draft) => {
    setStep(draft.step || 0);
    setCompany(draft.company || '');
    setTile(draft.tile || '');
    setContactId(draft.contact_id || '');
    setDealId(draft.deal_id || '');
    setSelectedTemplates(draft.selected_templates || []);
    setWizardData(draft.wizard_data || {});
    setManualInputs(draft.manual_inputs || {});
    setHasSecondOwner(draft.has_second_owner || false);
    setActiveDraftId(draft.id);
    setDraftName(draft.name || '');
    setShowDrafts(false);
    toast.success(`Loaded draft "${draft.name}"`);
  };

  const handleDeleteDraft = async (e, draftId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/uw/drafts/${draftId}`, { headers: getAuthHeaders() });
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      if (activeDraftId === draftId) setActiveDraftId(null);
      toast.success('Draft deleted');
    } catch { toast.error('Failed to delete draft'); }
  };

  // Union of placeholders from all selected templates — used for canNext validation
  const allTemplatePlaceholders = useMemo(() => {
    const phSet = new Set();
    templates.filter(t => selectedTemplates.includes(t.id)).forEach(t => {
      (t.placeholders || []).forEach(p => phSet.add(p));
    });
    return [...phSet];
  }, [templates, selectedTemplates]);

  // Intake shape derived from wizardData — passed to StepReview for resolve-data calls
  const wizardIntakeData = useMemo(() => ({
    advance: wizardData._advance || '',
    factor: wizardData._factor || '',
    term: wizardData._term || '',
    paymentFrequency: wizardData._paymentFrequency || wizardData.PF || 'Daily',
    fundingDate: wizardData._fundingDate || wizardData.DATE || '',
    originationFee: wizardData._originationFee || 0,
    brokerFee: wizardData._brokerFee || 0,
    otherFees: 0,
    netFundingOverride: '',
    paymentCountOverride: '',
    merchantName: wizardData.BUSINESS_NAME || '',
    dba: wizardData.DBA || '',
    contactName: wizardData.MERCHANT_NAME || '',
    email: wizardData.EMAIL || '',
    phone: wizardData.PHONE || '',
    address: wizardData.BIZ_ADDRESS || wizardData.BUSINESS_ADDRESS || '',
    ein: wizardData.EIN || '',
  }), [wizardData]);

  // If user doesn't have access, show access denied message
  if (!hasUWToolkitAccess) {
    return (
      <Layout>
        <div className="p-6 max-w-2xl mx-auto text-center">
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600">Access Denied</CardTitle>
              <CardDescription>
                You do not have access to UW Toolkit. Please contact an administrator to enable this feature for your account.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </Layout>
    );
  }

  const canNext = () => {
    if (step === 0) return !!company;
    if (step === 1) return !!tile;
    if (step === 2) return !!contactId;
    if (step === 3) return selectedTemplates.length > 0;
    if (step === 4) {
      const pf = wizardData._paymentFrequency || wizardData.PF || 'Daily';
      // Weekly frequency requires a weekday to be selected
      if (pf === 'Weekly' && !wizardData.WEEKDAY) return false;
      // Financial fields are required if any financial placeholder is in scope
      const hasFinancialSection = WIZARD_SECTIONS.financial?.fields.some(
        f => allTemplatePlaceholders.includes(f.key)
      );
      if (hasFinancialSection) {
        const adv = Number(wizardData._advance);
        const fac = Number(wizardData._factor);
        const trm = Number(wizardData._term);
        if (!(adv > 0 && fac >= 1 && trm > 0)) return false;
      }
      // Deposit section: all 4 months required when templates need deposit-derived fields.
      // Also requires advance/factor/term (needed to compute PERCENT_OF_INCOME correctly).
      const needsDeposit = allTemplatePlaceholders.includes('AVG_MONTHLY_INCOME') ||
                           allTemplatePlaceholders.includes('PERCENT_OF_INCOME');
      if (needsDeposit) {
        const adv = Number(wizardData._advance);
        const fac = Number(wizardData._factor);
        const trm = Number(wizardData._term);
        if (!(adv > 0 && fac >= 1 && trm > 0)) return false;
        const deps = [wizardData._dep1, wizardData._dep2, wizardData._dep3, wizardData._dep4];
        if (!deps.every(d => d !== '' && d !== undefined && Number(d) > 0)) return false;
      }
      return true;
    }
    return true;
  };

  const reset = () => {
    setStep(0);
    setCompany('');
    setTile('');
    setDealId('');
    setContactId('');
    setContactPrefill(null);
    setSelectedTemplates([]);
    setTemplates([]);
    setManualInputs({});
    setWizardData({});
    setHasSecondOwner(false);
    setActiveDraftId(null);
    setDraftName('');
  };

  // Bridge wizard placeholder values to manualInputs before entering StepReview.
  // wizardData values form the base; any pre-existing manualInputs take precedence.
  const handleNext = () => {
    if (step === 4) {
      const wizardManualInputs = {};
      for (const [k, v] of Object.entries(wizardData)) {
        if (!k.startsWith('_') && v !== '' && v !== null && v !== undefined) {
          wizardManualInputs[k] = String(v);
        }
      }
      // Compute deposit-derived fields — AVG_MONTHLY_INCOME and PERCENT_OF_INCOME
      // are calculated on the frontend and injected as manual_overrides (no resolver changes)
      const needsDeposit = allTemplatePlaceholders.includes('AVG_MONTHLY_INCOME') ||
                           allTemplatePlaceholders.includes('PERCENT_OF_INCOME');
      if (needsDeposit) {
        const deps = [
          Number(wizardData._dep1),
          Number(wizardData._dep2),
          Number(wizardData._dep3),
          Number(wizardData._dep4),
        ];
        if (deps.every(d => d > 0)) {
          const avg = deps.reduce((s, d) => s + d, 0) / 4;
          const pf = wizardData._paymentFrequency || wizardData.PF || 'Daily';
          const adv = Number(wizardData._advance);
          const fac = Number(wizardData._factor);
          const trm = Number(wizardData._term);
          let dp = 0;
          if (adv > 0 && fac >= 1 && trm > 0) {
            const c = calculateIntake({
              advance: adv, factor: fac, term: trm,
              paymentFrequency: pf,
              originationFee: Number(wizardData._originationFee) || 0,
              brokerFee: Number(wizardData._brokerFee) || 0,
              otherFees: 0,
              fundingDate: wizardData._fundingDate || wizardData.DATE,
            });
            dp = c.paymentAmount || 0;
          }
          const monthlyPayment = pf === 'Weekly' ? dp * 4 : dp * 20;
          const poi = avg > 0 ? (monthlyPayment / avg) * 100 : 0;
          wizardManualInputs.AVG_MONTHLY_INCOME = `$${avg.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          wizardManualInputs.PERCENT_OF_INCOME = `${poi.toFixed(2)}%`;
        }
      }
      setManualInputs(prev => ({ ...wizardManualInputs, ...prev }));
    }
    setStep(s => s + 1);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const payload = {
        deal_id: dealId,
        contact_id: contactId,
        company,
        template_ids: selectedTemplates,
        manual_inputs: manualInputs,
        has_second_owner: hasSecondOwner,
      };
      // Attach intake data from wizard if financial fields are filled
      const adv = Number(wizardData._advance);
      const fac = Number(wizardData._factor);
      const trm = Number(wizardData._term);
      if (adv > 0 && fac >= 1 && trm > 0) {
        payload.intake_data = {
          advance: adv,
          factor: fac,
          term: trm,
          payment_frequency: wizardData._paymentFrequency || wizardData.PF || 'Daily',
          origination_fee: Number(wizardData._originationFee) || 0,
          broker_fee: Number(wizardData._brokerFee) || 0,
          other_fees: 0,
          net_funding_override: null,
          payment_count_override: null,
          funding_date: wizardData._fundingDate || wizardData.DATE || null,
          merchant_name: wizardData.BUSINESS_NAME || null,
          dba: wizardData.DBA || null,
          contact_name: wizardData.MERCHANT_NAME || null,
          email: wizardData.EMAIL || null,
          phone: wizardData.PHONE || null,
          address: wizardData.BIZ_ADDRESS || wizardData.BUSINESS_ADDRESS || null,
          ein: wizardData.EIN || null,
        };
      }
      const res = await axios.post(
        `${API}/uw/generate`,
        payload,
        { headers: getAuthHeaders(), responseType: 'blob' }
      );

      const zblWarning = res.headers['x-zbl-warning'];
      if (zblWarning) toast.warning(zblWarning);

      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `UW_${company}_${dealId}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 200);
      toast.success('PDF generated successfully');
      // Record usage for all selected templates (non-blocking)
      selectedTemplates.forEach(tid => {
        axios.post(`${API}/uw/templates/${tid}/record-usage`, {}, { headers: getAuthHeaders() }).catch(() => {});
      });
    } catch (e) {
      let msg = 'Generation failed';
      try {
        if (e.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          try {
            const json = JSON.parse(text);
            if (json.detail?.error === 'strict_validation_failed') {
              const missing = json.detail.missing_placeholders || [];
              msg = `${missing.length} placeholder(s) need values: ${missing.join(', ')}`;
              toast.error(msg);
              return;
            }
            msg = typeof json.detail === 'string' ? json.detail : JSON.stringify(json.detail);
          } catch {
            msg = text || msg;
          }
        } else if (e.response?.data?.detail) {
          const detail = e.response.data.detail;
          if (detail?.error === 'strict_validation_failed') {
            const missing = detail.missing_placeholders || [];
            msg = `${missing.length} placeholder(s) need values: ${missing.join(', ')}`;
            toast.error(msg);
            return;
          }
          msg = typeof detail === 'string' ? detail : JSON.stringify(detail);
        } else if (e.message) {
          msg = e.message;
        }
      } catch (e) { console.error('Parse error:', e); /* fallback to default */ }
      toast.error(typeof msg === 'string' ? msg.slice(0, 200) : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveToDrive = async () => {
    setSavingToDrive(true);
    try {
      const payload = {
        deal_id: dealId,
        company,
        template_ids: selectedTemplates,
        manual_inputs: manualInputs,
        has_second_owner: hasSecondOwner,
      };
      const adv = Number(wizardData._advance);
      const fac = Number(wizardData._factor);
      const trm = Number(wizardData._term);
      if (adv > 0 && fac >= 1 && trm > 0) {
        payload.intake_data = {
          advance: adv, factor: fac, term: trm,
          payment_frequency: wizardData._paymentFrequency || wizardData.PF || 'Daily',
          origination_fee: Number(wizardData._originationFee) || 0,
          broker_fee: Number(wizardData._brokerFee) || 0,
          other_fees: 0,
          net_funding_override: null,
          payment_count_override: null,
          funding_date: wizardData._fundingDate || wizardData.DATE || null,
          merchant_name: wizardData.BUSINESS_NAME || null,
          dba: wizardData.DBA || null,
          contact_name: wizardData.MERCHANT_NAME || null,
          email: wizardData.EMAIL || null,
          phone: wizardData.PHONE || null,
          address: wizardData.BIZ_ADDRESS || wizardData.BUSINESS_ADDRESS || null,
          ein: wizardData.EIN || null,
        };
      }
      const res = await axios.post(`${API}/uw/generate-and-save-to-drive`, payload, { headers: getAuthHeaders() });
      const { drive_link, filename } = res.data;
      toast.success(
        <div>
          <p className="font-medium">Saved to Google Drive</p>
          <a href={drive_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm">{filename}</a>
        </div>,
        { duration: 8000 }
      );
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Failed to save to Google Drive');
    } finally {
      setSavingToDrive(false);
    }
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="p-6 text-center text-gray-500">
          <p className="text-lg font-medium">Access Denied</p>
          <p className="text-sm">UW Toolkit is available to admin users only.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Layers className="h-6 w-6 text-blue-600" />
            UW Toolkit
          </h1>
          <p className="text-sm text-gray-500">Google Doc–based document generation engine</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDrafts(v => !v)}
            data-testid="toggle-drafts-btn"
          >
            <FolderOpenIcon className="h-4 w-4 mr-1" />
            Drafts {drafts.length > 0 && <Badge variant="secondary" className="ml-1 text-xs h-4 px-1">{drafts.length}</Badge>}
          </Button>
          {step > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setDraftName(activeDraftId ? (drafts.find(d => d.id === activeDraftId)?.name || '') : ''); setSaveDraftOpen(true); }}
              data-testid="save-draft-btn"
            >
              <Save className="h-4 w-4 mr-1" />
              {activeDraftId ? 'Update Draft' : 'Save Draft'}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowAdmin(!showAdmin)} data-testid="toggle-admin-btn">
            <Settings2 className="h-4 w-4 mr-1" />
            {showAdmin ? 'Hide Admin' : 'Template Admin'}
          </Button>
        </div>
      </div>

      {showAdmin && <TemplateAdmin getAuthHeaders={getAuthHeaders} />}

      {/* ── Drafts Panel ── */}
      {showDrafts && (
        <Card className="border-slate-200 bg-slate-50" data-testid="drafts-panel">
          <CardHeader className="pb-2 pt-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <FolderOpenIcon className="h-4 w-4 text-slate-500" /> Saved Drafts
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowDrafts(false)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            {drafts.length === 0 ? (
              <p className="text-xs text-slate-500 italic py-2">No saved drafts yet. Start a questionnaire and click "Save Draft".</p>
            ) : (
              <div className="space-y-1.5">
                {drafts.map(d => (
                  <div
                    key={d.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors group ${d.id === activeDraftId ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:border-blue-300 hover:bg-blue-50'}`}
                    onClick={() => handleLoadDraft(d)}
                    data-testid={`draft-item-${d.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-800 truncate">{d.name}</span>
                        {d.id === activeDraftId && <Badge className="text-[10px] bg-blue-100 text-blue-700 border-0 px-1.5">Active</Badge>}
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                        {d.company && <span>{d.company}</span>}
                        {d.contact_name && <span>· {d.contact_name}</span>}
                        <span>· Step {(d.step || 0) + 1}</span>
                        <span>· {new Date(d.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <button
                      className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => handleDeleteDraft(e, d.id)}
                      title="Delete draft"
                      data-testid={`delete-draft-${d.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Save Draft Dialog ── */}
      <Dialog open={saveDraftOpen} onOpenChange={setSaveDraftOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{activeDraftId ? 'Update Draft' : 'Save Draft'}</DialogTitle>
            <DialogDescription>Save your current questionnaire progress to continue later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm">Draft Name <span className="text-red-500">*</span></Label>
              <Input
                value={draftName}
                onChange={e => setDraftName(e.target.value)}
                placeholder={`${company || 'PFS'} — ${contactPrefill?.company_name || 'Contact Name'}`}
                className="mt-1"
                data-testid="draft-name-input"
                onKeyDown={e => { if (e.key === 'Enter' && draftName.trim()) handleSaveDraft(); }}
                autoFocus
              />
            </div>
            <p className="text-xs text-slate-500">
              Progress: Step {step + 1} of {STEPS.length}
              {company && ` · ${company}`}
              {contactPrefill?.company_name && ` · ${contactPrefill.company_name}`}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDraftOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveDraft}
              disabled={!draftName.trim() || savingDraft}
              data-testid="confirm-save-draft-btn"
            >
              {savingDraft ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving…</> : <><Save className="h-4 w-4 mr-1" />Save Draft</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stepper */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              {STEPS.map((s, i) => (
                <React.Fragment key={s}>
                  <button
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      i === step ? 'bg-blue-600 text-white' :
                      i < step ? 'bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200' :
                      'bg-gray-100 text-gray-400'
                    }`}
                    onClick={() => i < step && setStep(i)}
                    disabled={i > step}
                    data-testid={`step-${i}`}
                  >
                    {i + 1}. {s}
                  </button>
                  {i < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-gray-300" />}
                </React.Fragment>
              ))}
            </div>
            {step > 0 && (
              <Button variant="ghost" size="sm" onClick={reset} data-testid="reset-btn">
                Start Over
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-6 pb-8">
          {step === 0 && <StepCompany value={company} onChange={setCompany} />}
          {step === 1 && <StepTile value={tile} onChange={setTile} />}
          {step === 2 && (
            <StepContact
              onContactSelect={(id, prefill) => { setContactId(id); setContactPrefill(prefill); }}
              onDealSelect={setDealId}
              getAuthHeaders={getAuthHeaders}
            />
          )}
          {step === 3 && (
            <StepTemplateSelect
              company={company}
              tile={tile}
              selected={selectedTemplates}
              onChange={setSelectedTemplates}
              getAuthHeaders={getAuthHeaders}
              templates={templates}
              setTemplates={setTemplates}
            />
          )}
          {step === 4 && (
            <StepWizard
              dealId={dealId}
              contactPrefill={contactPrefill}
              company={company}
              templates={templates}
              selectedTemplates={selectedTemplates}
              wizardData={wizardData}
              setWizardData={setWizardData}
              hasSecondOwner={hasSecondOwner}
              setHasSecondOwner={setHasSecondOwner}
              getAuthHeaders={getAuthHeaders}
            />
          )}
          {step === 5 && (
            <StepReview
              company={company}
              tile={tile}
              dealId={dealId}
              contactId={contactId}
              templateIds={selectedTemplates}
              manualInputs={manualInputs}
              setManualInputs={setManualInputs}
              onGenerate={handleGenerate}
              onSaveToDrive={handleSaveToDrive}
              generating={generating}
              savingToDrive={savingToDrive}
              getAuthHeaders={getAuthHeaders}
              intakeData={wizardIntakeData}
              hasSecondOwner={hasSecondOwner}
              setHasSecondOwner={setHasSecondOwner}
            />
          )}
        </CardContent>

        {/* Navigation */}
        {step < 5 && (
          <div className="px-6 pb-6 flex justify-between">
            <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={step === 0} data-testid="prev-step-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <Button onClick={handleNext} disabled={!canNext()} data-testid="next-step-btn">
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </Card>
    </div>
    </Layout>
  );
}
